/**
 * Made-up history for the casino page: daily trends, messages to the owner and
 * status changes. Generated from the casino's id, so a casino always
 * shows the same numbers. Everything here is invented and every part that shows
 * it carries a DemoPill.
 */
import type { CasinoCard } from '@/types/api';

/** A small seeded generator (FNV-1a hash, then xorshift): the same seed gives the same sequence. */
const seeded = (seed: string) => {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h ^= h << 13;
    h ^= h >>> 17;
    h ^= h << 5;
    return ((h >>> 0) % 10000) / 10000;
  };
};

export type MockOwnerMessage = {
  id: string;
  title: string;
  channels: ('email' | 'telegram' | 'popup')[];
  sentAt: string;
  outcome: 'opened' | 'delivered' | 'clicked';
};

const MESSAGES: Omit<MockOwnerMessage, 'id' | 'sentAt'>[] = [
  { title: 'Welcome to your new casino', channels: ['email', 'telegram'], outcome: 'opened' },
  { title: 'New: live dealer studio', channels: ['email', 'popup'], outcome: 'clicked' },
  { title: 'October product update', channels: ['email'], outcome: 'delivered' },
  { title: 'Tips for your first 100 players', channels: ['email', 'telegram'], outcome: 'opened' },
];

/** Messages the owner received, newest first. */
export const mockOwnerMessages = (c: CasinoCard): MockOwnerMessage[] => {
  const rnd = seeded(c.id + ':messages');
  const count = 2 + Math.floor(rnd() * 3);
  return MESSAGES.slice(0, count)
    .map((m, i) => ({ ...m, id: `${c.id}-m${i}`, sentAt: new Date(Date.UTC(2026, 9, 25 - i * 9, 9 + i, 0)).toISOString() }))
    .sort((a, b) => b.sentAt.localeCompare(a.sentAt));
};

export type MockStatusChange = { at: string; label: string; detail: string };

/** Paid, deploying, live, oldest first; anchored on the real launch date when there is one. */
export const mockStatusTimeline = (c: CasinoCard): MockStatusChange[] => {
  const live = c.launched_at ? new Date(c.launched_at) : new Date(Date.UTC(2026, 9, 1));
  const daysBefore = (n: number) => new Date(live.getTime() - n * 86400000).toISOString();
  const steps: MockStatusChange[] = [
    { at: daysBefore(12), label: 'Paid', detail: 'The owner paid for the casino in the Owner Hub wizard.' },
    { at: daysBefore(9), label: 'Deploying', detail: 'Content deployment started.' },
  ];
  if (c.status === 'ok') steps.push({ at: live.toISOString(), label: 'Live', detail: 'The casino went live on its domain.' });
  return steps;
};
